/**
 * Shared tags (#662): a Parameter node holds one widget, and any node's code
 * names it as `[!! @name !!]`. A Parameter node has no edge, so the nodes it
 * reaches are found by reading their code, here.
 */
import { effectiveValue, nodeRunKey, normalizeWidgets, type WidgetDef } from "../widgets/widgetModel";
import { getUnversionedFlowNodeType } from "../flowNodeCanonicalType";
import { renameSharedReferences, selectionNamesIn, sharedNamesIn } from "./codeReferences";
import { normalizeSelections } from "./selectionTags";

/** Kept in sync with `PARAMETER_TYPE` in `execution/workflow_spec.py`. */
export const PARAMETER_NODE_TYPE = "curio.builtin/parameter";

type FlowNodeLike = { id: string; type?: string | null; data?: any };

/** Whether *node*, a canvas node or a spec node, is a Parameter node. */
export function isParameterNode(node: { type?: string | null; data?: any }): boolean {
  return getUnversionedFlowNodeType(node) === PARAMETER_NODE_TYPE;
}

/**
 * The well-formed widgets in *raw*, each read on its own. Unlike a node's own
 * widgets, two with one name are both kept, so a reference to that name says
 * so. Kept in sync with `normalize_shared` in `code_references.py`.
 */
export function normalizeShared(raw: unknown): WidgetDef[] {
  return Array.isArray(raw) ? raw.flatMap((entry) => normalizeWidgets([entry])) : [];
}

/** The widgets the Parameter nodes among the canvas *nodes* hold, in node order. */
export function sharedWidgetsOf(nodes: readonly FlowNodeLike[]): WidgetDef[] {
  return nodes.filter(isParameterNode).flatMap((node) => normalizeShared(node.data?.widgets));
}

/** The same, for the nodes of a saved spec, whose widgets are at `metadata.widgets`. */
export function sharedWidgetsOfSpec(nodes: readonly { type?: string; metadata?: any }[]): WidgetDef[] {
  return nodes.filter(isParameterNode).flatMap((node) => normalizeShared(node.metadata?.widgets));
}

/** A canvas node's code as its editor holds it. */
export function nodeCode(node: FlowNodeLike): string {
  if (typeof node.data?.code === "string") return node.data.code;
  return typeof node.data?.defaultCode === "string" ? node.data.defaultCode : "";
}

/** The nodes among *nodes* whose code names the shared tag *name*. */
export function nodesUsingShared<N extends FlowNodeLike>(nodes: readonly N[], name: string): N[] {
  return nodes.filter((node) => !isParameterNode(node) && sharedNamesIn(nodeCode(node)).includes(name));
}

/** What a shared tag's hover says of its Parameter node: the node's title, and
 * how many nodes read it. Null when no Parameter node holds *name*. */
export function parameterPreviewOf(nodes: readonly FlowNodeLike[], name: string): { node: string; readers: number } | null {
  const holder = nodes.find(
    (node) => isParameterNode(node) && normalizeShared(node.data?.widgets).some((widget) => widget.name === name),
  );
  if (!holder) return null;
  const title = typeof holder.data?.title === "string" ? holder.data.title.trim() : "";
  return { node: title || "Parameter", readers: nodesUsingShared(nodes, name).length };
}

/**
 * What a run of a node depends on: `nodeRunKey` (its code and its widgets'
 * values), the values of the shared tags its code names, and what the node's
 * selection tags its code names hold (*selections*, the node's
 * `data.selections`). Code that names none keeps `nodeRunKey`'s key, so no
 * saved result goes stale; a new selection changes the key.
 */
export function runKeyWithShared(code: string, widgets: unknown, shared: WidgetDef[], selections?: unknown): string {
  let key = nodeRunKey(code, widgets);
  const names = sharedNamesIn(code);
  if (names.length > 0) {
    const values = names.map((name) => [name, shared.filter((w) => w.name === name).map(effectiveValue)]);
    key += "\u0000@" + JSON.stringify(values);
  }
  const picked = selectionNamesIn(code);
  if (picked.length > 0) {
    const tags = normalizeSelections(selections);
    const held = picked.map((name) => {
      const tag = tags.find((t) => t.name === name);
      return [name, tag ? (Array.isArray(tag.ids) ? tag.ids : { count: tag.count }) : null];
    });
    key += "\u0000selection" + JSON.stringify(held);
  }
  return key;
}

/** A canvas node's run key as its *data* holds it: its code, widgets and selection tags. */
export function runKeyOfData(data: any, shared: WidgetDef[]): string {
  return runKeyWithShared(String(data?.code ?? ""), data?.widgets, shared, data?.selections);
}

/**
 * Every canvas node's run key now. A run on the server reads them just before
 * the save it runs (useServerRun), so a step's result is stamped with the key
 * its run started with, not one a value changed meanwhile gives.
 */
export function runKeysOf(nodes: readonly FlowNodeLike[]): Map<string, string> {
  const shared = sharedWidgetsOf(nodes);
  return new Map(nodes.map((node) => [node.id, runKeyOfData(node.data, shared)]));
}

/**
 * *nodes* after the Parameter node named *from* was renamed *to*: each node
 * whose code names it gets the code rewritten, in `data.code` and in
 * `data.defaultCode`, which its editor shows. Other nodes are kept as they are.
 */
export function withSharedRenamed<N extends FlowNodeLike>(nodes: N[], from: string, to: string): N[] {
  return nodes.map((node) => {
    if (isParameterNode(node)) return node;
    const code = nodeCode(node);
    const renamed = renameSharedReferences(code, from, to);
    if (renamed === code) return node;
    return { ...node, data: { ...node.data, code: renamed, defaultCode: renamed } };
  });
}
